import { useTranslation } from "react-i18next";
import { Field } from "@/atoms/field";
import { Select } from "@/atoms/select";
import { regionOptions } from "@/utils/language";
import type { CreateCheckoutPaymentData } from "@/data/checkout";

/**
 * Who is paying, and where they are (checkout 1.4 region `form`).
 *
 * One column and one column only: the template's doNot forbids two columns beyond expiry and
 * CVC, and those two belong to Stripe. Every field is the shared `Field` atom, so the money
 * form has exactly one input implementation and the card row above it matches it by
 * construction rather than by a second stylesheet.
 *
 * The email is validated on blur and again on submit, never on every keystroke, and the sentence
 * that comes back says what is wrong with the address rather than that something is.
 */

export interface Address {
  line1: string;
  line2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

export const EMPTY_ADDRESS: Address = {
  line1: "",
  line2: "",
  city: "",
  state: "",
  postal_code: "",
  country: "",
};

/** The form's address in the shape the backend has always taken. */
export function toApiAddress(
  address: Address,
): CreateCheckoutPaymentData["address"] {
  return {
    line1: address.line1,
    line2: address.line2 || undefined,
    city: address.city,
    state: address.state || undefined,
    postal_code: address.postal_code,
    country: address.country,
  };
}

export interface BillingFieldsProps {
  values: { email: string; name: string; phone: string; address: Address };
  emailError: string | null;
  collectPhoneNumber: boolean;
  collectBillingAddress: boolean;
  onEmail: (value: string) => void;
  onEmailBlur: () => void;
  onName: (value: string) => void;
  onPhone: (value: string) => void;
  onAddress: (address: Address) => void;
}

export function BillingFields({
  values,
  emailError,
  collectPhoneNumber,
  collectBillingAddress,
  onEmail,
  onEmailBlur,
  onName,
  onPhone,
  onAddress,
}: BillingFieldsProps) {
  const { t, i18n } = useTranslation();
  const { email, name, phone, address } = values;

  return (
    <>
      <Field
        label={t("gymkeePay.checkout.emailLabel")}
        type="email"
        inputMode="email"
        autoComplete="email"
        required
        value={email}
        onChange={(event) => onEmail(event.target.value)}
        onBlur={onEmailBlur}
        error={emailError}
      />
      <Field
        label={t("gymkeePay.checkout.nameLabel")}
        autoComplete="name"
        value={name}
        onChange={(event) => onName(event.target.value)}
      />
      {collectPhoneNumber && (
        <Field
          label={t("gymkeePay.checkout.phoneLabel")}
          type="tel"
          autoComplete="tel"
          required
          value={phone}
          onChange={(event) => onPhone(event.target.value)}
        />
      )}
      {collectBillingAddress && (
        <>
          <Field
            label={t("gymkeePay.checkout.addressLabel")}
            autoComplete="address-line1"
            required
            value={address.line1}
            onChange={(event) =>
              onAddress({ ...address, line1: event.target.value })
            }
          />
          <Field
            label={t("gymkeePay.checkout.addressLine2Label")}
            autoComplete="address-line2"
            value={address.line2}
            onChange={(event) =>
              onAddress({ ...address, line2: event.target.value })
            }
          />
          <Field
            label={t("gymkeePay.checkout.city")}
            autoComplete="address-level2"
            required
            value={address.city}
            onChange={(event) =>
              onAddress({ ...address, city: event.target.value })
            }
          />
          <Field
            label={t("gymkeePay.checkout.postalCode")}
            autoComplete="postal-code"
            required
            value={address.postal_code}
            onChange={(event) =>
              onAddress({ ...address, postal_code: event.target.value })
            }
          />
          <Field
            label={t("gymkeePay.checkout.regionLabel")}
            autoComplete="address-level1"
            value={address.state}
            onChange={(event) =>
              onAddress({ ...address, state: event.target.value })
            }
          />
          <Select
            label={t("gymkeePay.checkout.countryLabel")}
            placeholder={t("gymkeePay.checkout.countryPlaceholder")}
            required
            value={address.country}
            options={regionOptions(i18n.language).map((region) => ({
              value: region.code,
              label: region.name,
            }))}
            onChange={(event) =>
              onAddress({ ...address, country: event.target.value })
            }
          />
        </>
      )}
    </>
  );
}
